import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";

import Collapse from 'react-bootstrap/Collapse';

import {
  updateSelectedDeviceColor,
  updateSelectedInclusionColor,
} from "Redux/actions/dealDetailsAction";

import { connect, CustomContainer } from "../../atoms/DependencyHelper";
import styles from "./DealHighLights.module.scss";
import dealDetailsBodyStyles from "../dealDetailsBody/DealDetailsBody.module.scss";

import ColorSwatch, { ColorSwatchModes, NotificationPositions } from 'components/molecules/colorSwatch/ColorSwatch';
import DealDetailsPromotions from "components/molecules/dealDetailsPromotions/dealDetailsPromotions";

import {
  formatBytes,
  getBadgeName,
  getProductImageUrl,
  getTagName,
  imagePrefix,
} from "utils/helperServices";
import { useScreenType } from "utils/hooks/common";

import DeviceSubsection from "./DeviceSubsection";

import LabelText from "../../atoms/label/LabelText";
import SvgWithText from "../../molecules/iconWithText/svgWithText";

import types from "../../../config/types";
import { ScreenSizeTypes } from "../../../config/constants";

const MaxInclusionCount = 3;

const DefaultHighLights = [
  {
    id: "1",
    value: "",
    displayUnit: "Anytime data",
    unit: "promo_data",
    iconLabel: "data",
  },
  {
    id: "2",
    value: "",
    displayUnit: "Night Owl data",
    unit: "nightowl_units",
    iconLabel: "data",
  },
  {
    id: "3",
    value: "",
    displayUnit: "minutes",
    unit: "minutes_label",
    iconLabel: "phone",
  },
  {
    id: "4",
    value: "",
    displayUnit: "airtime",
    unit: "airtime",
    iconLabel: "combo",
  },
  {
    id: "5",
    value: "",
    displayUnit: "SMS",
    unit: "sms",
    iconLabel: "sms",
  },
];
const DefaultNotice = {
  text: "",
  description: "",
  visible: false,
};

const DealHighlights = ({
  dealData,
  primaryProduct,
  primaryProductColors,
  secondaryProductColors,
  dealInclusionColors,
  moreDeals: inclusions,
  planData,
  secondaryProduct,
  selectedPrimaryDeviceColor: primarySwatchColor,
  selectedSecondaryDeviceColor: secondarySwatchColor,
  updateSelectedDeviceColor,
  updateSelectedInclusionColor,
}) => {
  const [open, setOpen] = useState(false);
  const [inclusionNotice, setInclusionNotice] = useState(DefaultNotice);
  const [productNotice, setProductNotice] = useState(DefaultNotice);
  const [isLoading, setIsLoading] = useState(true);

  const screenType = useScreenType();
  const isMobile = screenType === ScreenSizeTypes.Mobile;
  const isDesktop = screenType === ScreenSizeTypes.Desktop;

  const deviceImagePlaceHolder = getProductImageUrl(dealData, "image", "No_image");
  const hasInclusions = Array.isArray(inclusions) && inclusions.length > 0;
  const hasSecondaryProduct = typeof secondaryProduct === "object" && Object.keys(secondaryProduct).length > 0;
  const isLinearDisplay = (Array.isArray(inclusions) && inclusions.length <= MaxInclusionCount);
  const showAdditionalInfo = !!dealData?.compatible_routers;

  const subsectionCol = (isLinearDisplay && hasInclusions)
    ? "col-lg-4"
    : "col-lg-6";
  const inclusionCol = isLinearDisplay
    ? "col-lg-12"
    : "col-lg-6";

  const additionalInfo = typeof dealData?.compatible_routers === "string"
    ? dealData?.compatible_routers.split("\r\n")
    : [];

  useEffect(() => {
    setIsLoading(false);
  }, []);
    
  const getHighlights = (item) => {
    const highlights = DefaultHighLights.map(highlight => ({ ...highlight }));
    highlights.forEach((highlight) => {
      highlight.value = item[highlight?.unit];
    });

    const newHighlight = highlights.filter((item) => {
      if (item.value > 0 || item.value == "Unlimited") {
        return item;
      }
    });

    const dataIndex = newHighlight.findIndex(
      (highLight) => highLight.unit === "promo_data"
    );

    if (dataIndex >= 0) {
      newHighlight[dataIndex].value = formatBytes(
        newHighlight[dataIndex].value
      );
    }

    const airtimeIndex = newHighlight.findIndex(
      (highLight) => highLight.unit === "airtime"
    );

    if (airtimeIndex >= 0) {
      newHighlight[airtimeIndex].value = `R${newHighlight[airtimeIndex].value}`;
    }

    const nightData = newHighlight.findIndex(
      (highLight) => highLight.unit === "nightowl_units"
    );

    if (nightData >= 0) {
      newHighlight[nightData].value =
        planData?.nightowl_uom != null
          ? `${newHighlight[nightData]?.value}${planData?.nightowl_uom}`
          : `${newHighlight[nightData].value}`;

      if (dataIndex >= 0) {
        newHighlight[dataIndex].owlValue = newHighlight[nightData].value;
        newHighlight[dataIndex].owlDisplayUnit =
          newHighlight[nightData].displayUnit;

        return newHighlight.filter((item) => item.unit !== "nightowl_units");
      }
    }

    return newHighlight;
  };

  const handleColorChange = (primaryColor, secondaryColor) => {
    typeof updateSelectedDeviceColor === "function"
      && updateSelectedDeviceColor({
        primaryDeviceColor: primaryColor,
        secondaryDeviceColor: secondaryColor
      });
  }

  const handleColorInclusionChange = (inclusionSku, inclusionColor) => {
    setInclusionNotice({
      text: inclusionColor?.notification?.text || "",
      description: inclusionColor?.notification?.description || "",
      visible: !!(inclusionColor?.notification?.text && inclusionColor?.notification?.description)
    });

    typeof updateSelectedInclusionColor === "function"
      && updateSelectedInclusionColor(inclusionSku, inclusionColor);
  }

  const showInclusionColorSwatch = (inclusionColor) => !!(inclusionColor
    && Array.isArray(inclusionColor.options)
  );

  const renderProduct = (product, deviceLabel, colorOptions, isPrimaryDevice = true) => {

    const onColorChange = (color) => {
      setProductNotice({
        text: color?.notification?.text || "",
        description: color?.notification?.description || "",
        visible: !!(color?.notification?.text && color?.notification?.description)
      });

      isPrimaryDevice
        ? handleColorChange(color, null)
        : handleColorChange(null, color);
    }

    const deviceImage = isPrimaryDevice
      ? primarySwatchColor?.product?.image?.url
      : secondarySwatchColor?.product?.image?.url;
    const showColorSwatch = !dealData.sim_only_flg
      && Array.isArray(colorOptions)
      && !!colorOptions.length;

    const colorSwatch = showColorSwatch
      ? (
        <ColorSwatch
          alignNotification={NotificationPositions.Bottom}
          className={styles["color-swatch"]}
          options={colorOptions}
          selectedOption={isPrimaryDevice ? primarySwatchColor : secondarySwatchColor}
          onColorSelect={onColorChange}
          swatchMode={ColorSwatchModes.Rounded}
          toggleNotice={() => setProductNotice({ ...productNotice, visible: !productNotice.visible })}
        />
      )
      : null;

    return (
      <>
        <LabelText className={`d-block ${styles["device-title"]}`}>{deviceLabel}:</LabelText>
        <CustomContainer className="d-flex">
          <CustomContainer className={`${styles["device"]} ${styles["primary-device"]}`}>
            <img className={styles["primary-device-img"]} src={deviceImage || deviceImagePlaceHolder} />
          </CustomContainer>

          <CustomContainer className={styles["device-details"]}>
            <CustomContainer className={`pl-s ${styles.title}`}>
              { product?.name }
            </CustomContainer>

            { !isMobile && hasSecondaryProduct && colorSwatch }
          </CustomContainer>
        </CustomContainer>

        { (isMobile || !hasSecondaryProduct) && colorSwatch }
      </>
    );
  };

  const renderPlanDetails = () => {
    if (!planData) {
      return null;
    }

    const planHighlights = getHighlights(planData);
    const highlighCol = planHighlights.length <= 2 && screenType === ScreenSizeTypes.Tablet
      ? "col-md-6"
      : "col-md-12";

    return (
      <CustomContainer>
        <LabelText className={`d-block ${styles["plan-title"]}`}>Plan:</LabelText>
        <LabelText className={styles.title}>{planData?.name}</LabelText>

        <CustomContainer className={styles["plan-details"]}>
          { planHighlights.map((item, index) => (
            <SvgWithText
              key={`svg_${index}`}
              iconType={item.iconLabel}
              className={highlighCol}
              description={item.displayUnit}
              owlValue={item.owlValue}
              owlDescription={item.owlDisplayUnit}
              value={item.value}
            />
          )) }
        </CustomContainer> 
      </CustomContainer>
    );
  }

  const renderInclusions = () => {
    if (!hasInclusions) return;

    const extractInclusionImg = (colorOption, inclusion) => {
      if (!colorOption?.product?.image?.url) {
        return inclusion.image?.url;
      }

      return colorOption.product?.image?.url;
    }

    return (
      <>
        <LabelText className={`d-block ${styles["device-inclusion-title"]}`}>
          Also includes:
        </LabelText>

        <CustomContainer className={styles["device-inclusion-container"]}>
          { inclusions.map((item, index) => (
            <CustomContainer
              key={`itm_${index}`}
              className={`col-xs-12 col-md-6 ${inclusionCol} d-flex 
                ${styles["device-inclusion"]}
                ${isLinearDisplay ? styles["linear-display"] : ""}
              `}
            >
              <CustomContainer
                className={styles["device"]}
              >
                <img
                  className={styles["device-img"]}
                  src={extractInclusionImg(dealInclusionColors[item.sku]?.selectedColor, item)}
                />
              </CustomContainer>

              <CustomContainer className="ml-s">
                <CustomContainer className={styles.title}>
                  {item?.name}
                </CustomContainer>

                { showInclusionColorSwatch(dealInclusionColors[item.sku + index]) && (
                  <ColorSwatch
                    alignNotification={NotificationPositions.Right}
                    options={dealInclusionColors[item.sku + index].options}
                    selectedOption={dealInclusionColors[item.sku + index].selectedColor}
                    onColorSelect={(color) => handleColorInclusionChange(item.sku + index, color)}
                    swatchMode={ColorSwatchModes.Rounded}
                    toggleNotice={() => setInclusionNotice({ ...inclusionNotice, visible: !inclusionNotice.visible })}
                  />
                )}
              </CustomContainer>

              { (isDesktop && isLinearDisplay) && index < (inclusions.length - 1) && 
                <CustomContainer className={styles["inclusion-separator"]}> + </CustomContainer>
              }
            </CustomContainer>
          ))}
        </CustomContainer>
      </>
    );
  }

  return (
    isLoading 
    ? <div className={`${dealDetailsBodyStyles["loading_placeholder"]}`}>
        <div className={`${dealDetailsBodyStyles["animated-background"]}`} ></div>
      </div> 
    : <>
      <CustomContainer className={`d-none d-lg-block ${styles["deal-highlights-divider"]}`} />
      { primaryProduct && (
        <CustomContainer className={styles["product-details"]}>
          <CustomContainer className={styles["device-section"]}>
            <CustomContainer className={`col-sm-12 ${styles["device-header"]}`}>
              <LabelText className={styles["device-header--name"]}>{dealData?.name}</LabelText>
              {dealData?.promotion_tag != null && dealData?.promotion_tag_label !== types.TYPE_PREORDER && (
                <DealDetailsPromotions
                  variant={getBadgeName(getTagName(dealData?.promotion_tag_label))}
                  tagText={dealData?.promotion_tag_label}
                  labelColor={dealData?.promotion_tag_swatch}
                />
              )}
            </CustomContainer>

            <DeviceSubsection
              className={subsectionCol}
              footerText={productNotice.visible ? productNotice.description : ""}
              onDismissFooter={() => setProductNotice({ ...productNotice, visible: false })}
            >
            
              { renderProduct(primaryProduct, hasSecondaryProduct ? "Primary device" : "Device", primaryProductColors) }
              { hasSecondaryProduct && renderProduct(secondaryProduct, "Secondary device", secondaryProductColors, false) }
    
            </DeviceSubsection>

            <DeviceSubsection className={subsectionCol}>
              { /** plan */} 
              { renderPlanDetails() }
            </DeviceSubsection>

            { hasInclusions &&
              <DeviceSubsection
                className={isLinearDisplay ? subsectionCol : "col-md-12"}
                footerText={inclusionNotice.visible ? inclusionNotice.description : ""}
                onDismissFooter={() => setInclusionNotice({ ...inclusionNotice, visible: false})}
              >
                { /** also includes */}
                { renderInclusions() }
              </DeviceSubsection>
            }

            { showAdditionalInfo && (
              <DeviceSubsection className={'col-sm-12'}>
                <CustomContainer
                  className={styles["collapsible-header"]}
                  onClick={() => setOpen(!open)}
                >
                  <LabelText className={styles["collapsible-header--label"]}>
                    { "Only select routers are compatible for use with our Large Data Tariffs" }
                  </LabelText>

                  <LabelText aria-expanded={open}>
                    { !open
                      ? <span className={`${styles["toggle-indicator--label"]}`}>
                        { dealData?.compatible_routers_lable || "View more info" }
                        </span>
                      : <span className={styles["toggle-indicator--hidden"]}>Hide</span>
                    }

                    <img
                      className={`${styles["toggle-indicator--icon"]} ${open ? styles["open"] : ""}`}
                      src={`${imagePrefix}/assets/images/caret-down.svg`}
                    />
                  </LabelText>
                </CustomContainer>

                <Collapse in={open}>
                  <CustomContainer className={`${styles["collapsible-content"]}`}>
                    <LabelText>{ dealData?.compatible_routers_heading || "" }:</LabelText>
                    <ul className={styles["collapsible-content--list-items"]}>
                      { additionalInfo.map((item, index) => (<li key={`info-item-${index}`}>{ item }</li>)) }
                    </ul>
                  </CustomContainer> 
                </Collapse>
              </DeviceSubsection>
            )}
          </CustomContainer>
        </CustomContainer>
      )}
      </>
  );
};

DealHighlights.propTypes = {
  isDealDetailPage: PropTypes.bool,
  
  // Redux
  dealData: PropTypes.object,
  dealDetails: PropTypes.object,
  dealInclusionColors: PropTypes.object,
  primaryProduct: PropTypes.object,
  primaryProductColors: PropTypes.arrayOf(PropTypes.object),
  moreDeals: PropTypes.arrayOf(PropTypes.object),
  planData: PropTypes.object,
  secondaryProduct: PropTypes.object,
  secondaryProductColors: PropTypes.arrayOf(PropTypes.object),
  selectedPrimaryDeviceColor: PropTypes.object,
  selectedSecondaryDeviceColor: PropTypes.object,
  updateSelectedDeviceColor: PropTypes.func,
  updateSelectedInclusionColor: PropTypes.func,
};

DealHighlights.defaultProps = {
  primaryProduct: null,
  moreDeals: [],
  planData: null,
};

const mapStateToProps = (state) => ({
  dealData: state.dealDetails.selectedDealDetails,
  dealInclusionColors: state.dealDetails.dealInclusionColors,
  primaryProduct: state.dealDetails.primaryProduct,
  primaryProductColors: state.dealDetails.primaryProductColors,
  moreDeals: state.dealDetails.dealInclusions,
  planData: state.dealDetails.dataPlanDetails,
  secondaryProduct: state.dealDetails.secondaryProduct,
  secondaryProductColors: state.dealDetails.secondaryProductColors,
  selectedPrimaryDeviceColor: state.dealDetails.selectedPrimaryDeviceColor,
  selectedSecondaryDeviceColor: state.dealDetails.selectedSecondaryDeviceColor,
})

const mapDispatchToProps = ({
  updateSelectedDeviceColor,
  updateSelectedInclusionColor,
});

export default connect(mapStateToProps, mapDispatchToProps)(DealHighlights);
